import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { hasPlanFeature, getPlanLimit, getPlanDefinition } from '../utils/planAccess';
import UpgradeModal from './UpgradeModal';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useQrOrders } from '../context/QrOrdersContext';
import { canFiscalRelay, fiscalForward } from '../utils/printService';
import { offlineEnabled } from '../utils/offlineCache';
import { isOffline as netIsOffline } from '../utils/netStatus';
import FiskalKassaModal from './FiskalKassaModal';
import BanquetPrintDialog from './BanquetPrintDialog';

export default function KassirSidebar({ open = false, onClose = () => {} }) {
    const navigate  = useNavigate();
    const location  = useLocation();

    // Close sidebar on route change (mobile)
    useEffect(() => { onClose(); }, [location.pathname, location.search]); // eslint-disable-line
    const { user }  = useAuthStore();
    const { qrCount, openModal: openQrModal } = useQrOrders() || {};

    const plan           = user?.tenant_plan;
    const planDef        = getPlanDefinition(plan);
    const [upgradeModal, setUpgradeModal] = useState(false);

    // Offline rejim izləmə — etibarlı net-status (C# navigator.onLine yanlışdır) + 5 san poll
    const [netOffline, setNetOffline] = useState(netIsOffline());
    useEffect(() => {
        const sync = () => setNetOffline(netIsOffline());
        window.addEventListener('online', sync);
        window.addEventListener('offline', sync);
        window.addEventListener('net-status', sync); // netMonitor keçidi — dərhal
        const iv = setInterval(sync, 5000);
        return () => { window.removeEventListener('online', sync); window.removeEventListener('offline', sync); window.removeEventListener('net-status', sync); clearInterval(iv); };
    }, []);
    const offlineMode = offlineEnabled() && netOffline;
    // Offline bağlanmış, fiskal çeki kəsilməmiş hesablar (yalnız offline tikli tenantda, online ikən)
    const [fiscalPendingCount, setFiscalPendingCount] = useState(0);
    useEffect(() => {
        if (!offlineEnabled()) return undefined;
        let alive = true;
        const loadFp = () => {
            if (netIsOffline()) return;
            api.get('/orders/fiscal-pending').then((r) => { if (alive) setFiscalPendingCount((r.data?.data || []).length); }).catch(() => {});
        };
        loadFp();
        window.addEventListener('app-resync', loadFp);
        return () => { alive = false; window.removeEventListener('app-resync', loadFp); };
    }, []);

    // Banket menyu çapı — yalnız aktiv banket varsa düymə görünür
    // Siyahı sessionStorage-da saxlanır: menyu hər səhifədə yenidən qurulur, düymə gec görünsə
    // altındakı düymələr aşağı «tullanır» və sürüşmə yeri pozulur. Keşdən dərhal, serverdən arxada.
    const BQ_CACHE = 'kassir_active_banquets';
    const [activeBanquets, setActiveBanquets] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(BQ_CACHE) || '[]'); } catch { return []; }
    });
    const [banquetPrintOpen, setBanquetPrintOpen] = useState(false);
    useEffect(() => {
        if (!hasPlanFeature(plan, 'menu_sets')) return undefined;
        let alive = true;
        api.get('/banquet').then((r) => {
            if (!alive) return;
            const list = (r.data?.data || []).filter((b) => Number(b.active) === 1)
                .map((b) => ({ id: b.id, name: b.name, price_per_person: b.price_per_person, active: b.active }));
            setActiveBanquets(list);
            try { sessionStorage.setItem(BQ_CACHE, JSON.stringify(list)); } catch { /* ignore */ }
        }).catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);

    const hasRental      = hasPlanFeature(plan, 'rental');
    const hasPurchases   = hasPlanFeature(plan, 'purchases');
    const hasExpenses    = hasPlanFeature(plan, 'expenses');
    const hasStaff       = hasPlanFeature(plan, 'staff');
    const hasReports     = hasPlanFeature(plan, 'reports');
    const hasKassaLedger = hasPlanFeature(plan, 'kassa_ledger');
    const hasSatis       = hasPlanFeature(plan, 'satis');
    const hasOfis        = hasPlanFeature(plan, 'ofis');
    const hasCashReg     = hasPlanFeature(plan, 'cash_register');
    const hasWarehouse   = hasPlanFeature(plan, 'warehouse');

    // ── Sidebar scroll persistence ────────────────────────────────────────────
    const menuScrollRef = useRef(null);
    useEffect(() => {
        const saved = sessionStorage.getItem('kassir_sidebar_scroll');
        if (saved && menuScrollRef.current) menuScrollRef.current.scrollTop = Number(saved);
    }, []);
    const onMenuScroll = () => {
        if (menuScrollRef.current) sessionStorage.setItem('kassir_sidebar_scroll', menuScrollRef.current.scrollTop);
    };

    // ── Fiskal kassa state ────────────────────────────────────────────────────
    const [cashboxStatus,        setCashboxStatus]        = useState(null);
    const [cashboxModal,         setCashboxModal]         = useState(false);
    const [cashboxLoading,       setCashboxLoading]       = useState(false);
    const [cashboxActionLoading, setCashboxActionLoading] = useState('');


    const loadCashboxStatus = useCallback(async () => {
        try {
            const res = await api.get('/cashbox/status');
            setCashboxStatus(res.data?.data || null);
        } catch { setCashboxStatus(null); }
    }, []);

    const openCashboxModal = async () => {
        setCashboxModal(true);
        setCashboxLoading(true);
        await loadCashboxStatus();
        setCashboxLoading(false);
    };

    const runCashboxAction = async (endpoint, successMessage) => {
        setCashboxActionLoading(endpoint);
        try {
            if (canFiscalRelay()) {
                const prep = await api.post('/cashbox/prepare', { action: endpoint });
                const fwd = await fiscalForward(prep.data.relay);
                const done = await api.post('/cashbox/complete', { action: endpoint, raw: fwd.raw, status: fwd.status });
                toast.success(done.data?.msg || successMessage);
            } else {
                await api.post(`/cashbox/${endpoint}`);
                toast.success(successMessage);
            }
            await loadCashboxStatus();
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Xəta baş verdi.');
        } finally { setCashboxActionLoading(''); }
    };


    // ── helpers ───────────────────────────────────────────────────────────────
    const isActive = (path) => {
        if (!path) return false;
        const [pathname, qs] = path.split('?');
        if (location.pathname !== pathname) return false;
        if (!qs) return true;
        const need    = new URLSearchParams(qs);
        const current = new URLSearchParams(location.search);
        for (const [k, v] of need) { if (current.get(k) !== v) return false; }
        return true;
    };

    const Btn = ({ label, icon, path = '', color = 'default', onClick, locked, badge }) => {
        const active = isActive(path);
        const base   = 'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all w-full relative';

        const cls = locked
            ? `${base} bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed`
            : active
                ? `${base} bg-blue-600 text-white border-blue-600 shadow-sm`
                : color === 'green'
                    ? `${base} bg-green-600 text-white border-green-600 hover:bg-green-700`
                    : color === 'red'
                        ? `${base} bg-red-500 text-white border-red-500 hover:bg-red-600`
                        : `${base} bg-white text-slate-700 border-slate-200 hover:bg-slate-50`;

        return (
            <button
                onClick={locked ? () => setUpgradeModal(true) : (onClick || (() => navigate(path)))}
                className={cls}
            >
                <span className="text-sm leading-none shrink-0">{icon}</span>
                <span className="truncate leading-tight">{label}</span>
                {locked && <span className="ml-auto text-[10px]">🔒</span>}
                {badge > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                        {badge}
                    </span>
                )}
            </button>
        );
    };

    const Sec = ({ label }) => (
        <p className="pt-3 pb-1 px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {label}
        </p>
    );

    const Divider = () => <div className="my-1 border-t border-slate-200" />;

    // Offline rejim üçün ortaq header (inline)
    const OfflineHeader = () => (
        <div className="relative border-b border-slate-200 bg-white px-4 py-3 flex-shrink-0">
            <button onClick={onClose} className="absolute right-2 top-2 md:hidden flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-slate-500 text-base hover:bg-slate-200 transition-all">✕</button>
            <div className="flex justify-center mb-2">
                <span className="text-lg font-black tracking-tight text-slate-800">Fiscal<span className="text-blue-500"> POS</span></span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs">{user?.name?.[0] || '?'}</div>
                <div className="min-w-0">
                    <p className="truncate font-bold text-slate-800 text-xs">{user?.name}</p>
                    <p className="truncate text-[10px] text-slate-400">{user?.tenant_name}</p>
                </div>
            </div>
        </div>
    );

    // ── Offline view — yalnız offline işləyən bölmələr ──────────────────────────
    if (offlineMode) {
        return (
            <>
            {open && <div className="fixed inset-0 z-10 bg-black/40 md:hidden" onClick={onClose} />}
            <aside className={`fixed left-0 top-0 z-20 flex h-full w-64 flex-col border-r border-slate-200 bg-gray-50 shadow-sm transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                <OfflineHeader />
                <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1">
                    <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] font-bold text-amber-700 leading-snug">
                        📴 Offline rejim — yalnız sifarişlər işləyir. İnternet qayıdanda bütün bölmələr geri gəlir.
                    </div>
                    <Sec label="Sifariş" />
                    <Btn icon="🍽️" label="Masalar / Sifarişlər" path="/waiter/tables" />
                </div>
                <div className="border-t border-slate-200 p-2.5 flex-shrink-0">
                    <button onClick={() => useAuthStore.getState().logout(navigate)}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 py-2 text-xs font-bold text-red-500 hover:bg-red-500 hover:text-white transition-all">
                        🚪 Çıxış
                    </button>
                </div>
            </aside>
            </>
        );
    }

    return (
        <>
        {open && <div className="fixed inset-0 z-10 bg-black/40 md:hidden" onClick={onClose} />}
        <aside className={`fixed left-0 top-0 z-20 flex h-full w-64 flex-col border-r border-slate-200 bg-gray-50 shadow-sm transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>

            {/* Header */}
            <div className="relative border-b border-slate-200 bg-white px-4 py-3 flex-shrink-0">
                <button onClick={onClose} className="absolute right-2 top-2 md:hidden flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-slate-500 text-base hover:bg-slate-200 transition-all">✕</button>
                <div className="flex justify-center mb-2">
                    <span className="text-lg font-black tracking-tight text-slate-800">
                        Fiscal<span className="text-blue-500"> POS</span>
                    </span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs">
                        {user?.name?.[0] || '?'}
                    </div>
                    <div className="min-w-0">
                        <p className="truncate font-bold text-slate-800 text-xs">{user?.name}</p>
                        <p className="truncate text-[10px] text-slate-400">{user?.tenant_name}</p>
                    </div>
                </div>
            </div>

            {/* Scrollable menu */}
            <div ref={menuScrollRef} onScroll={onMenuScroll} className="flex-1 overflow-y-auto px-2.5 py-2 space-y-1">

                {/* Sifariş */}
                <Sec label="Sifariş" />
                <Btn icon="🍽️" label="Masalar / Sifarişlər" path="/waiter/tables" />
                <Btn
                    icon="📲"
                    label="QR Sifarişlər"
                    onClick={openQrModal || (() => navigate('/waiter/tables'))}
                    badge={qrCount}
                />

                <Divider />

                {/* Ümumi */}
                <Sec label="Ümumi" />
                <Btn icon="📊" label="Dashboard"   path="/admin/dashboard" />
                {hasReports && <Btn icon="📋" label="Hesabatlar" path="/admin/reports" />}
                {!hasReports && <Btn icon="📋" label="Hesabatlar" locked />}

                <Divider />

                {/* Kirayə */}
                <Sec label="Kirayə" />
                <div className="grid grid-cols-2 gap-1">
                    <Btn icon="🏠" label="Evlər"    path="/admin/rental?tab=evler"    locked={!hasRental} />
                    <Btn icon="🧑" label="Qonaqlar" path="/admin/rental?tab=qonaqlar" locked={!hasRental} />
                </div>

                <Divider />

                {/* Bazarlıq */}
                <Sec label="Bazarlıq" />
                <div className="grid grid-cols-2 gap-1">
                    <Btn icon="🛒" label="Alışlar" color="green" path="/admin/bazarliq?tab=alislar"  locked={!hasPurchases} />
                    <Btn icon="🧹" label="Digər"   color="red"   path="/admin/bazarliq?tab=diger"    locked={!hasPurchases} />
                </div>
                <Btn icon="🏢" label="Tədarükçülər" path="/admin/bazarliq?tab=tedarukculer" locked={!hasPurchases} />
                {hasWarehouse && <Btn icon="📋" label="Sayım" path="/admin/sayim" />}
                {activeBanquets.length > 0 && (
                    <Btn icon="🍽️" label="Banket menyu" onClick={() => setBanquetPrintOpen(true)} />
                )}
                {/* Stok idarəsi — yalnız kassir + Pro (warehouse) + superadmin icazəsi (read-only baxış) */}
                {hasWarehouse && Number(user?.kassir_stock_view) === 1 && <Btn icon="📦" label="Stok idarəsi" path="/warehouse/dashboard" />}

                <Divider />

                {/* Maliyyə */}
                <Sec label="Maliyyə" />
                {hasKassaLedger
                    ? <Btn icon="💰" label="Kassa Hesabatı" path="/admin/kassa-ledger" />
                    : <Btn icon="💰" label="Kassa Hesabatı" locked />}
                {hasSatis
                    ? <Btn icon="📦" label="Satış"          path="/admin/satis" />
                    : <Btn icon="📦" label="Satış"          locked />}
                {hasOfis
                    ? <Btn icon="🏢" label="Kassa → Ofis"   path="/admin/kassa-ofis" />
                    : <Btn icon="🏢" label="Kassa → Ofis"   locked />}
                {hasCashReg
                    ? <Btn icon="💳" label="Fiskal Kassa"   onClick={openCashboxModal} />
                    : <Btn icon="💳" label="Fiskal Kassa"   locked />}
                {offlineEnabled() && (
                    <Btn icon="🧾" label="Fiskal gözləyənlər" path="/admin/fiscal-pending" badge={fiscalPendingCount || undefined} />
                )}
                {hasExpenses && <Btn icon="💸" label="Xərclər" path="/admin/expenses" />}
                {/* Şifrələri: in grid with İşçilər when unlocked, full-width when locked */}
                {hasStaff ? (
                    <div className="grid grid-cols-2 gap-1">
                        <Btn icon="👥" label="İşçilər / Maaş" path="/admin/staff" />
                        <Btn icon="👤" label="Ofisiantlar"     path="/admin/users" />
                    </div>
                ) : (
                    <Btn icon="👤" label="Ofisiantlar" path="/admin/users" />
                )}
                {/* Locked at bottom */}
                {!hasExpenses && <Btn icon="💸" label="Xərclər"        locked />}
                {!hasStaff    && <Btn icon="👥" label="İşçilər / Maaş" locked />}

            </div>

            {/* Logout */}
            <div className="border-t border-slate-200 p-2.5 flex-shrink-0">
                {useAuthStore.getState().isImpersonating?.() ? (
                    // Superadmin kassir kimi girib → «Çıxış» əvəzinə geri qayıt
                    <button
                        onClick={() => { useAuthStore.getState().exitImpersonation(); navigate('/superadmin/impersonate'); }}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-200 bg-purple-50 py-2 text-xs font-bold text-purple-700 hover:bg-purple-600 hover:text-white transition-all"
                    >
                        ← Superadmin-ə qayıt
                    </button>
                ) : (
                    <button
                        onClick={() => useAuthStore.getState().logout(navigate)}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 py-2 text-xs font-bold text-red-500 hover:bg-red-500 hover:text-white transition-all"
                    >
                        🚪 Çıxış
                    </button>
                )}
            </div>
        </aside>

        {/* ── Upgrade Modal ────────────────────────────────────────────────── */}
        <UpgradeModal
            open={upgradeModal}
            onClose={() => setUpgradeModal(false)}
            title={`${planDef?.label || 'Pulsuz'} paket istifadə edirsiniz`}
            message="Bu funksiyadan istifadə etmək üçün planınızı yüksəldin."
            plans={['basic', 'pro']}
            currentPlan={plan}
        />

        {/* ── Fiskal Kassa Modal (ortaq komponent: Caspos+SmartOne+TapXPhone) ── */}
        <BanquetPrintDialog open={banquetPrintOpen} onClose={() => setBanquetPrintOpen(false)} banquets={activeBanquets} />
        <FiskalKassaModal open={cashboxModal} onClose={() => setCashboxModal(false)} />
        </>
    );
}
